import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, UserPlus, Mail, Phone, Building, Calendar } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

const STATUS_COLORS = {
  new: "bg-blue-500/10 text-blue-400",
  contacted: "bg-yellow-500/10 text-yellow-400",
  qualified: "bg-purple-500/10 text-purple-400",
  converted: "bg-green-500/10 text-green-400",
  lost: "bg-red-500/10 text-red-400",
};
const STATUS_LABELS = { new: "Nouveau", contacted: "Contacte", qualified: "Qualifie", converted: "Converti", lost: "Perdu" };

export default function AdminCatalogueLeadsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/catalogue-leads");
      setLeads(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.post(`/catalogue-leads/${lead_id}/status`, { status });
      toast.success("Statut mis a jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const filtered = filter ? leads.filter(l => l.status === filter) : leads;

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === "new").length,
    converted: leads.filter(l => l.status === "converted").length,
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="catalogue-leads-page">
      <PageHeader
        icon={<UserPlus />}
        title="Prospects catalogue"
        description="Demandes d'informations reçues depuis votre catalogue public."
        testId="catalogue-leads-header"
      />

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-400 uppercase font-bold">Total</div>
          <div className="text-3xl font-black">{stats.total}</div>
        </div>
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-400 uppercase font-bold">Nouveaux</div>
          <div className="text-3xl font-black text-blue-400">{stats.new}</div>
        </div>
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-400 uppercase font-bold">Convertis</div>
          <div className="text-3xl font-black text-green-400">{stats.converted}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1 rounded ${filter === "" ? "bg-[#f7c935] text-[#14101c] font-bold" : "bg-white/5 text-slate-400"}`}>Tous</button>
        {Object.keys(STATUS_LABELS).map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1 rounded ${filter === s ? "bg-[#f7c935] text-[#14101c] font-bold" : "bg-white/5 text-slate-400"}`} data-testid={`filter-${s}`}>
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#f7c935] mx-auto my-10" /> :
        filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">
            <UserPlus className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            Aucun prospect pour ce filtre.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(l => (
              <div key={l.lead_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`lead-${l.lead_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-white">{l.first_name} {l.last_name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_COLORS[l.status]}`}>{STATUS_LABELS[l.status]}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 mb-2">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>
                      {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>}
                      {l.company && <span className="flex items-center gap-1"><Building className="w-3 h-3" />{l.company}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{l.created_at?.slice(0, 10)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mb-2">Interesse par: <span className="text-[#f7c935]">{l.program_title}</span></div>
                    {l.message && <p className="text-sm bg-[#0b0811] rounded p-2 text-slate-300">{l.message}</p>}
                  </div>
                  <select
                    value={l.status}
                    onChange={e => updateStatus(l.lead_id, e.target.value)}
                    className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-xs"
                    data-testid={`status-${l.lead_id}`}
                  >
                    {Object.entries(STATUS_LABELS).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
